import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type {
  AdressAnsicht, ApiClient, BelegAdressWahl, BelegParteien as BelegParteienStand,
  Vorgangsart,
} from "@kundeninfo/api-client";
import { Feld } from "@kundeninfo/ui";
import { Auswahl, Hinweis } from "@kundeninfo/ui/me-design";
import { alsEintrag, alsText, geordnet } from "../adressText.js";
import type { KopffeldTabelle } from "./vorgangsdetail.js";
import { useInSection } from "./Vorgangsabschnitt.js";
import { SECTION_TEXT } from "./vorgangsabschnitt/texts.js";
import { errorProp } from "./fieldErrors.js";
import "./BelegParteien.css";

/**
 * Die PARTEIEN eines Belegs — EIN Baustein für den Kopfblock von Angebot, Auftrag und
 * Rechnung (Spec „Adressen und Konzernstruktur", 10.09.2026, „Beleg-Modal").
 *
 * **Er speichert NICHT selbst.** Er zeichnet und meldet seinen Entwurf nach oben; das
 * Speichern gehört dem Modal, in dem er steht — ein zweiter Speichern-Knopf im strikten
 * Panel wäre ein zweiter Ausgang, und die Hausregel kennt nur einen. `onEntwurf` reicht
 * den Stand hoch; die VERSION, mit der geschrieben wird, gehört dem Modal: Sie ist die
 * des Belegs selbst (`belegKopf.version` in repositories/belegAdresse.ts), und das Modal
 * bekommt sie beim Speichern des Kopfes frisch zurück. Eine hier gemeldete Version wäre
 * zum Zeitpunkt des Schreibens bereits überholt.
 *
 * **Die Fachlichkeit steckt NICHT hier.** Wer Käufer sein darf, welche Adresse an welchen
 * Platz passt, was ohne Wahl gilt — das entscheidet der Server (`belegAdressRules`,
 * `repositories/belegAdresse.ts`) und schickt es als `wahl`, `auswahl` und
 * `kaeuferKandidaten` mit. `web` darf `domain` nicht importieren
 * (scripts/check-boundaries.mjs), und soll es auch nicht: Eine zweite, abgeschriebene
 * Regel wäre eine, die eines Tages anders urteilt.
 *
 * **Eine Regel muss die Maske trotzdem kennen** — welche Adresse an welchem Platz
 * überhaupt ZUR WAHL steht (`wahlbar`). `auswahl` ist absichtlich ungefiltert (alle
 * Adressen des Käufers und des Kunden, jede Art, jede Rolle); der Schreibweg nimmt je
 * Platz aber nur dieselbe Art und die Rollen „eigene Rolle oder MAIN". Böte der Block
 * mehr an, schlüge die Wahl beim Speichern mit 422 BELEGADRESSE_UNGUELTIG fehl — an einer
 * Adresse, die die Maske selbst vorgeschlagen hat. Das ist keine zweite Fachregel, sondern
 * dieselbe Grenze von der anderen Seite; sie steht deshalb dicht am Kommentar, der ihre
 * Quelle nennt.
 *
 * **Eigener CSS-Block `bpa__`** — er gehört allein dieser Datei und reist mit ihr an jeden
 * der drei Einbauorte.
 *
 * **Er ist eine benannte GRUPPE** (`role="group"`, beschriftet von seiner eigenen
 * Überschrift). Das ist keine Kosmetik: Der Block steht in einer Maske voller anderer
 * Felder, und ein Bildschirmleser soll beim Betreten hören, dass hier die Parteien des
 * Belegs beginnen — dieselbe Bauart wie am Anschriftblock im Kopf der Kundenakte. Er gibt
 * zugleich den Durchstichen einen Ausschnitt, den sie über die Rolle ansprechen können,
 * statt über eine CSS-Klasse.
 */

/** Was der BAUSTEIN braucht: nur den Leseweg. */
export type BelegParteienClient = Pick<ApiClient, "getBelegParteien">;

/** Was ein EINBAUORT braucht: dazu den Schreibweg, den sein Speichern-Knopf geht. */
export type BelegParteienModalClient =
  BelegParteienClient & Pick<ApiClient, "setBelegParteien">;

/** Der Stand, den das Modal beim Speichern schickt — Feld für Feld der Rumpf von
 *  `setBelegParteien` ohne die Version. */
export type BelegParteienEntwurf = {
  billingCustomerId: string | null;
  billingAddressId: string | null;
  billingEmailAddressId: string | null;
  deliveryAddressId: string | null;
  ilnAddressId: string | null;
  purchaseOrderNumber: string | null;
};

export type BelegParteienProps = {
  client: BelegParteienClient;
  art: Vorgangsart;
  customerId: string;
  /** Ein Wechsel setzt ALLES zurück und lädt neu. */
  vorgangId: string;
  /**
   * Das Präfix der Feld-Kennungen — dasselbe, mit dem die Maske ihre übrigen Kopffelder
   * benennt. Kein `useId()`: Ein Server-Befund an `billingAddressId` soll den Kopfblock
   * aufklappen und GENAU dieses Feld fokussieren (`kopffeldId` + `PARTEIEN_KOPFFELDER`),
   * und dafür muss die Kennung von aussen vorhersagbar sein.
   */
  idPraefix: string;
  /** Über diesem Abschnitt läuft ein Schreibvorgang (oder die Maske steht im fremden
   *  Stand) — dann sind die Felder GESPERRT (readOnly + aria-disabled, der Fokus bleibt;
   *  seit ME-Etappe 3, Aufgabe 5, nicht mehr `disabled`). */
  verdeckt?: boolean;
  /**
   * Führt DIESE Maske die Bestellnummer selbst? Dann zeigt der Block sie nicht (Vorgabe:
   * er zeigt sie).
   *
   * Genau ein Einbauort tut das: der AUFTRAG. Er trägt sie seit jeher als eigene Spalte
   * (`salesOrder.customerPurchaseOrderNumber`), aus der bis Migration 024 auch die
   * Beleg-Adresse fortgeschrieben wird (`schreibeBestellnummerDurch`) — und aus der die
   * E-Rechnung ihr BT-13 nimmt. Zwei Eingabefelder für eine Zahl im selben Kopfblock
   * wären zwei Wahrheiten: Wer die eine änderte, sähe die andere überschreiben.
   */
  mitBestellnummer?: boolean;
  /**
   * Die Feldbefunde des Servers zum letzten Speicherversuch (SERVERNAME → Satz), aus
   * `ParteienGriff.befunde`.
   *
   * Sie stehen AM FELD und nicht nur am Fuss der Maske: Ein Befund an
   * `billingAddressId` ist eine Aussage über die Rechnungsanschrift, und ein
   * Bildschirmleser soll sie beim Betreten des Feldes hören (`aria-invalid` +
   * `aria-describedby`, Muster `Feld` in @kundeninfo/ui).
   */
  befunde?: Readonly<Record<string, string>>;
  /**
   * Zeichnet den Block LESEND — ohne ein einziges Bedienelement, auch an einem Beleg, der
   * nicht eingefroren ist.
   *
   * `verdeckt` reicht dafür NICHT: Es blendet die Felder ab, lässt sie aber im Baum
   * stehen, und ein abgeblendetes Auswahlfeld sieht aus wie etwas, das man ändern könnte,
   * wenn man nur wüsste wie (dieselbe Begründung wie am Lesemodus von `Feld` in
   * @kundeninfo/ui). Genau das braucht der LESE-Kasten der Rechnung: Er steht neben dem
   * Bearbeiten-Panel, und die Hausregel kennt nur EINEN Schreibweg — den im Panel.
   *
   * Der Satz „Dieser Beleg ist festgeschrieben" hängt weiterhin allein an
   * `stand.eingefroren`: Er ist eine Aussage über den Beleg, nicht über die Ansicht.
   */
  nurLesend?: boolean;
  /** Nach jeder EINGABE: Das Modal kennt den Stand, den es beim Speichern schickt.
   *  Ohne Eingabe kommt nichts — ein ungerührter Block soll kein PUT auslösen. */
  onEntwurf?: (entwurf: BelegParteienEntwurf) => void;
  /** Nach dem Laden: der Serverstand — für die Zusammenfassung im zugeklappten Abschnitt (Kunde bzw. Käufer). */
  onLoaded?: (stand: BelegParteienStand) => void;
};

/** Ein Platz am Beleg: sein deutscher Name, die Endung seiner Feld-Kennung und der Satz
 *  für „an dieser Stelle steht nirgends etwas". Der Satz steht ausgeschrieben da und wird
 *  nicht aus dem Namen gebogen — gebogene Sätze lesen sich wie übersetzte. */
type Platz = { name: string; idTeil: string; leer: string };

const PLAETZE: Readonly<Record<string, Platz>> = {
  billingAddressId: {
    name: "Rechnungsanschrift", idTeil: "rechnungsanschrift",
    leer: "Für die Rechnungsanschrift ist nichts hinterlegt.",
  },
  billingEmailAddressId: {
    name: "Rechnungs-E-Mail", idTeil: "rechnungs-email",
    leer: "Für die Rechnungs-E-Mail ist nichts hinterlegt.",
  },
  deliveryAddressId: {
    name: "Lieferort", idTeil: "lieferort",
    leer: "Für den Lieferort ist nichts hinterlegt.",
  },
  ilnAddressId: {
    name: "ILN des Käufers", idTeil: "iln",
    leer: "Für die ILN des Käufers ist nichts hinterlegt.",
  },
};

/**
 * SERVER-FELDNAME → Endung der Feld-Id, für die Felder DIESES Blocks — dieselbe Bauart
 * wie `ANGEBOT_KOPFFELDER` und Geschwister (`KopffeldTabelle` in vorgangsdetail.ts).
 *
 * Sie steht hier und nicht dreimal in den Masken: Der Block ist einer, seine Feldnamen
 * sind es auch, und ein Befund an `billingAddressId` soll an allen drei Einbauorten
 * dasselbe Feld aufschlagen.
 */
export const PARTEIEN_KOPFFELDER: KopffeldTabelle = {
  billingCustomerId: "kaeufer",
  billingAddressId: PLAETZE.billingAddressId!.idTeil,
  billingEmailAddressId: PLAETZE.billingEmailAddressId!.idTeil,
  deliveryAddressId: PLAETZE.deliveryAddressId!.idTeil,
  ilnAddressId: PLAETZE.ilnAddressId!.idTeil,
  purchaseOrderNumber: "bestellnummer",
};

/** Der Platz eines Feldes; ein unbekanntes Feld (neuer Platz am Server, ältere Maske)
 *  behält seinen technischen Namen, statt zu verschwinden. */
function platzFuer(feld: string): Platz {
  return PLAETZE[feld] ?? { name: feld, idTeil: feld,
    leer: `Für ${feld} ist nichts hinterlegt.` };
}

/**
 * Was an DIESEM Platz zur Wahl steht: dieselbe Art, und die eigene Rolle des Platzes oder
 * `MAIN` — die Grenze, die der Schreibweg zieht (`rollenFuerSlot` in
 * repositories/belegAdresse.ts). Ein Platz der Rolle `MAIN` (die ILN) kennt nur `MAIN`.
 */
function wahlbar(liste: AdressAnsicht[], w: BelegAdressWahl): AdressAnsicht[] {
  const rollen: string[] = w.role === "MAIN" ? ["MAIN"] : [w.role, "MAIN"];
  return geordnet(liste.filter((a) => a.kind === w.kind && rollen.includes(a.role)),
                  w.role);
}

/** Die Adressen des KÄUFERS tragen Rechnungsanschrift und ILN, die des KUNDEN den
 *  Lieferort — die Tochter bleibt Leistungsempfängerin (Spec). */
function quelleFuer(stand: BelegParteienStand, w: BelegAdressWahl): AdressAnsicht[] {
  return w.role === "DELIVERY" ? stand.auswahl.kunde : stand.auswahl.kaeufer;
}

/*
 * HIER STAND `beschriebenVon` — die Kennungen für `aria-describedby` (Befund + Auskunftszeile)
 * an den rohen `<select>`. Seit ME-Etappe 3 (Aufgabe 5) sind die Felder `Auswahl`
 * (me-auswahl): Den BEFUND trägt das Element selbst (`fehler` → `aria-invalid` +
 * `aria-describedby` im Shadow DOM). Die AUSKUNFT (Vorgabe, Erläuterung) ist seit me-design
 * c9c0c08 (Befund 15) das `hilfe` der Auswahl — gedämpft unter dem Feld und wieder in
 * `aria-describedby`. Nur die LEER-Zeile („nichts hinterlegt") bleibt ein eigener Absatz:
 * Sie ist ein Mangel in Warnfarbe, keine leise Auskunft.
 */

/** Die Auskunft unter einem Adressfeld als `hilfe`, oder `null` (dann die Leer-Zeile bzw.
 *  nichts, wenn gewählt ist). */
function auskunftFuer(gewaehlt: string | null, gesperrt: boolean,
  rueckfall: AdressAnsicht | null, ausBeziehung: boolean): string | null {
  if (gewaehlt !== null) return null;
  if (gesperrt) {
    return "Zur Wahl steht hier erst wieder etwas, wenn der Käuferwechsel gespeichert ist; "
      + "bis dahin gilt die Vorgabe des neuen Käufers.";
  }
  if (rueckfall === null) return null;
  // Was ohne Wahl gilt, steht DA — es zu erraten ist die häufigste Rückfrage an einer
  // Rückfallkette.
  return `${ausBeziehung ? "Vorgabe aus der Kundenbeziehung" : "Vorgabe"}: ${alsText(rueckfall)}`
    + (rueckfall.origin !== null ? ` (von ${rueckfall.origin.companyName})` : "");
}

/** Eine Anschrift als Block — Zeile für Zeile, wie sie auf dem Beleg steht. */
function alsBlock(a: AdressAnsicht): ReactElement {
  const zeilen = a.zeilen.length > 0 ? a.zeilen : [a.value ?? "—"];
  return (
    <>
      {zeilen.map((z, i) => <span key={`${i}-${z}`} className="bpa__zeile">{z}</span>)}
    </>
  );
}

export function BelegParteien(props: BelegParteienProps): ReactElement {
  const { client, art, customerId, vorgangId, idPraefix, verdeckt = false,
          mitBestellnummer = true, nurLesend = false, befunde = {}, onEntwurf, onLoaded } = props;
  /** Im Vorgangsabschnitt trägt der Abschnitt den Titel; die Gruppe bleibt über `aria-label` benannt. */
  const inSection = useInSection();
  const [stand, setStand] = useState<BelegParteienStand | null>(null);
  const [entwurf, setEntwurf] = useState<BelegParteienEntwurf | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Der Riegel gegen Antworten, die zu spät kommen (Muster `AdressListe`): Jeder Abruf
   *  merkt sich beim Start die laufende Nummer und schreibt sein Ergebnis nur, wenn sie
   *  beim Eintreffen noch dieselbe ist. */
  const lauf = useRef(0);
  const getBelegParteien = client.getBelegParteien;
  /** Der Rückruf in einer Ref (Muster `Zahlplan`): in den Abhängigkeiten lüde jede neue Funktionsidentität neu. */
  const reportLoaded = useRef(onLoaded);
  reportLoaded.current = onLoaded;

  /**
   * EIN Lade-Effekt, der beim Wechsel ALLES zurücksetzt: Kein UI-Zustand überlebt einen
   * Kontextwechsel, und der Wechsel des Vorgangs ist hier der schärfste — der Entwurf des
   * vorigen Belegs an einem anderen weiterzureichen hiesse, fremde Anschriften
   * hineinzutragen.
   */
  useEffect(() => {
    const meiner = ++lauf.current;
    setStand(null);
    setEntwurf(null);
    setHinweis(null);
    setFehler(null);
    getBelegParteien(art, customerId, vorgangId)
      .then((antwort) => {
        if (lauf.current !== meiner) return;
        setStand(antwort);
        setEntwurf(entwurfAus(antwort));
        reportLoaded.current?.(antwort);
      })
      .catch(() => {
        if (lauf.current !== meiner) return;
        setFehler("Die Parteien dieses Belegs konnten nicht geladen werden.");
      });
    return () => { lauf.current++; };
  }, [getBelegParteien, art, customerId, vorgangId]);

  /**
   * Eine Eingabe übernehmen und nach oben melden.
   *
   * Die Meldung steht AUSSERHALB des `setEntwurf`-Aktualisierers (Fixrunde 1): Ein
   * Aktualisierer muss frei von Nebenwirkungen sein — React ruft ihn unter StrictMode
   * absichtlich doppelt auf, und `onEntwurf` liefe dann zweimal je Tastendruck (Muster
   * `views/verwaltung/angebotsvorlagen.tsx`). Gerechnet wird deshalb hier, aus dem
   * Entwurf, den der Aufrufer ohnehin gerade in der Hand hält.
   */
  function setze(
    stand: BelegParteienEntwurf, teil: Partial<BelegParteienEntwurf>,
  ): void {
    const neu = { ...stand, ...teil };
    setEntwurf(neu);
    onEntwurf?.(neu);
  }

  /**
   * Der KÄUFERWECHSEL ist ein Kontextwechsel im Kleinen: Die Adressen des alten Käufers
   * stehen dem Beleg nicht mehr zur Verfügung. Rechnungsanschrift und ILN fallen deshalb
   * auf die Vorgabe zurück — sichtbar und mit Hinweis, nicht still. Der LIEFERORT bleibt:
   * Er gehört dem Kunden und ist vom Käufer unabhängig (Spec).
   */
  function kaeuferWechseln(
    aktuell: BelegParteienStand, stand: BelegParteienEntwurf, neuer: string | null,
  ): void {
    // Der RÜCKWEG (Fixrunde 1): Wer den ursprünglichen Käufer wieder einstellt, hat nicht
    // gewechselt — dann gelten wieder dessen Adressen, die geladene Wahl kommt zurück, und
    // der Hinweis verschwindet. Ein stehengebliebener Hinweis über einem Stand, den es
    // nicht mehr gibt, ist eine Falschaussage.
    if (neuer === aktuell.billingCustomerId) {
      const geladen = entwurfAus(aktuell);
      setze(stand, { billingCustomerId: neuer,
                     billingAddressId: geladen.billingAddressId,
                     billingEmailAddressId: geladen.billingEmailAddressId,
                     ilnAddressId: geladen.ilnAddressId });
      setHinweis(null);
      return;
    }
    setze(stand, { billingCustomerId: neuer, billingAddressId: null,
                     billingEmailAddressId: null, ilnAddressId: null });
    setHinweis("Der Käufer wurde gewechselt — Rechnungsanschrift, Rechnungs-E-Mail und ILN wurden "
      + "zurückgesetzt und stehen erst wieder zur Wahl, wenn dieser Beleg gespeichert ist; "
      + "dann mit den Adressen des neuen Käufers. Bis dahin gilt an diesen Plätzen die "
      + "Vorgabe. Der alte Käufer lässt sich jederzeit wieder einstellen.");
  }

  if (fehler !== null) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (stand === null || entwurf === null) {
    return <p className="bpa__laden">Parteien werden geladen …</p>;
  }

  if (stand.eingefroren || nurLesend) {
    return <ParteienLesend stand={stand}
                           mitBestellnummer={mitBestellnummer} />;
  }

  /**
   * Ein noch nicht gespeicherter Käuferwechsel: Die geladene `auswahl.kaeufer` und die
   * Rückfälle der beiden Käufer-Plätze gehören dann dem ALTEN Käufer. Sie weiter
   * anzubieten hiesse, eine Wahl vorzuschlagen, die der Schreibweg mit 422 abweist —
   * und eine Vorgabe zu nennen, die gar nicht gilt.
   */
  const kaeuferGewechselt = entwurf.billingCustomerId !== stand.billingCustomerId;

  return (
    <div className={inSection ? "bpa bpa--in-section" : "bpa"} role="group" aria-label={SECTION_TEXT.parties}>
      {!inSection && <h4 className="bpa__ueberschrift">{SECTION_TEXT.parties}</h4>}

      {/* Die Käuferwahl NUR mit Kette (Spec): An einem Kunden ohne übergeordneten Kunden
          wäre sie eine Frage mit genau einer Antwort — an jedem Beleg jedes Kunden.

          ODER mit einem GESPEICHERTEN Käufer, auch wenn er kein Kandidat ist (Fixrunde
          nach Gesamtreview, I2): Ein für diesen Bearbeiter nicht mehr einsehbarer Käufer
          bleibt am Beleg stehen (F4), erscheint aber in `kaeuferKandidaten` nicht. Ohne
          diese zweite Bedingung verschwände die Wahl ganz — und mit ihr die einzige
          Möglichkeit, ihn zu ENTFERNEN, die F4 ausdrücklich offenhält. */}
      {(stand.kaeuferKandidaten.length > 0 || stand.billingCustomerId !== null) && (
        <div className="bpa__feld">
          <Auswahl id={`${idPraefix}-kaeufer`} name={`${idPraefix}-kaeufer`}
            beschriftung="Käufer" wert={entwurf.billingCustomerId ?? ""}
            hilfe={"Die Rechnung kann an ein übergeordnetes Unternehmen gehen. "
              + "Leistungsort und Leistungsempfänger bleiben der Kunde."}
            gesperrt={verdeckt} {...errorProp(befunde.billingCustomerId)}
            onGeaendert={(d) => kaeuferWechseln(
              stand, entwurf, d.wert === "" ? null : d.wert)}>
            <option value="">Der Kunde selbst</option>
            {/* Der GESPEICHERTE Käufer zuerst, wenn ihn die Kandidatenliste nicht nennt
                (I2): Sonst stünde das Feld auf einem Wert, für den es keine Option gibt,
                und das `<select>` zeigte „Der Kunde selbst", während der Entwurf die
                Holding hält — eine Anzeige, die beim nächsten Speichern zur Tat würde. */}
            {stand.billingCustomerId !== null
              && !stand.kaeuferKandidaten.some((k) => k.id === stand.billingCustomerId) && (
              <option value={stand.billingCustomerId}>
                {kaeuferEintrag(stand.billingCustomerName ?? "Gespeicherter Käufer",
                                stand.billingCustomerArchiviert)} — gespeichert
              </option>
            )}
            {stand.kaeuferKandidaten.map((k) => (
              <option key={k.id} value={k.id}>
                {kaeuferEintrag(k.companyName, k.archiviert)}
              </option>
            ))}
          </Auswahl>
        </div>
      )}

      {/* Der archivierte Käufer: eine WARNUNG, kein Riegel (Dirk-Entscheid 12.09.2026).
          Ein archivierter Kunde ist kein gelöschter — die offene Forderung an ihn will
          bezahlt werden, der Beleg darf also an ihn gehen. Nur soll es niemand versehentlich
          tun. Sie hängt am ENTWURF und nicht am geladenen Stand: Wer auf einen lebenden
          Käufer wechselt, hat den Grund der Warnung beseitigt, und eine stehenbleibende
          Warnung wäre eine Falschaussage (Muster „der Rückweg" in `kaeuferWechseln`). */}
      {kaeuferArchiviert(stand, entwurf.billingCustomerId) && (
        <Hinweis art="warnung" className="bpa__archivwarnung">
          Der Käufer ist archiviert. Der Beleg kann trotzdem an ihn gehen.
        </Hinweis>
      )}

      {hinweis !== null && <Hinweis art="info" className="bpa__hinweis">{hinweis}</Hinweis>}

      {stand.wahl.map((w) => {
        const platz = platzFuer(w.feld);
        const vomKaeufer = w.role !== "DELIVERY";
        const gesperrt = kaeuferGewechselt && vomKaeufer;
        const liste = gesperrt ? [] : wahlbar(quelleFuer(stand, w), w);
        const gewaehlt = entwurf[w.feld as keyof BelegParteienEntwurf];
        const befund = befunde[w.feld];
        /**
         * Die GESPEICHERTE Wahl, wenn sie in `liste` nicht vorkommt (Fixrunde nach
         * Gesamtreview, C1). Zwei Fälle führen dazu: die KORREKTURRECHNUNG, deren Wahl auf
         * einer eingefrorenen Kopie steht (die hängt an keinem Kunden, also kennt `auswahl`
         * sie nicht), und ein Käufer, der für diesen Bearbeiter nicht mehr einsehbar ist
         * (I2 — seine Adressen sind keine Kandidaten mehr).
         *
         * Ohne diese Option zeigte das `<select>` den leeren Wert und damit „Vorgabe
         * verwenden", während der Entwurf die Wahl weiterhin hält — und das nächste
         * Speichern löste eine Wahl, die niemand angefasst hat. Der Schreibweg nimmt die
         * UNVERÄNDERTE Wahl ausdrücklich an (`setzeBelegParteien`), sie ist also kein
         * Vorschlag, der in einer 422 endet — deshalb darf sie hier stehen.
         *
         * Sie steht DAUERHAFT da, nicht nur solange das Feld auf ihr steht: Wer probehalber
         * etwas anderes wählt, muss zurückkönnen (Muster „der Rückweg" in
         * `kaeuferWechseln`) — eine Option, die beim ersten Wechsel verschwindet, wäre eine
         * Einbahnstrasse, aus der nur ein Neuladen herausführt. NICHT jedoch bei
         * `gesperrt`: Nach einem noch nicht gespeicherten Käuferwechsel gehört die alte
         * Wahl dem alten Käufer, und die würde der Schreibweg zu Recht abweisen.
         */
        const eigeneWahl = !gesperrt && w.gewaehlt !== null
          && !liste.some((a) => a.id === w.gewaehlt!.id) ? w.gewaehlt : null;
        const auskunft = auskunftFuer(gewaehlt, gesperrt, w.rueckfall, w.rueckfallAusBeziehung);
        return (
          <div className="bpa__feld" key={w.feld}>
            <Auswahl id={`${idPraefix}-${platz.idTeil}`} name={`${idPraefix}-${platz.idTeil}`}
              beschriftung={platz.name} wert={gewaehlt ?? ""}
              {...(auskunft === null ? {} : { hilfe: auskunft })}
              gesperrt={verdeckt} {...errorProp(befund)}
              onGeaendert={(d) => setze(entwurf, {
                [w.feld]: d.wert === "" ? null : d.wert,
              } as Partial<BelegParteienEntwurf>)}>
              <option value="">Vorgabe verwenden</option>
              {eigeneWahl !== null && (
                <option value={eigeneWahl.id}>{alsEintrag(eigeneWahl)} — gewählt</option>
              )}
              {liste.map((a) => (
                <option key={a.id} value={a.id}>{alsEintrag(a)}</option>
              ))}
            </Auswahl>
            {gewaehlt === null && !gesperrt && w.rueckfall === null && (
              <p className="bpa__leer" id={`${idPraefix}-${platz.idTeil}-auskunft`}>
                {platz.leer}
              </p>
            )}
          </div>
        );
      })}

      {/* Die Bestellnummer wird GETIPPT, nicht gewählt: Sie gehört diesem Beleg und steht
          an keinem Kunden. */}
      {/* `Feld` (me-eingabe): Befund, Erläuterung (`hilfe`, in aria-describedby) und die
          weiche Obergrenze von 35 Zeichen (`max`, Zähler — das Feld begrenzt nichts, der
          Server prüft; vorher `maxLength`) bringt das Element. */}
      {mitBestellnummer && (
        <Feld id={`${idPraefix}-bestellnummer`} label="Bestellnummer des Kunden"
          bearbeitbar wert={entwurf.purchaseOrderNumber ?? ""} max={35}
          gesperrt={verdeckt} fehler={befunde.purchaseOrderNumber}
          hilfe="Die Nummer, unter der der Kunde diesen Beleg führt."
          onChange={(wert) => setze(entwurf, {
            purchaseOrderNumber: wert === "" ? null : wert })} />
      )}
    </div>
  );
}

/**
 * Die LESENDE Fassung: kein einziger Knopf, kein Feld (Festlegung F3). Ein abgeblendetes
 * Auswahlfeld sähe aus wie etwas, das man ändern könnte, wenn man nur wüsste wie —
 * dieselbe Begründung wie am Lesemodus von `Feld` (@kundeninfo/ui).
 *
 * Zwei Wege führen hierher: ein EINGEFRORENER Beleg (`stand.eingefroren`) und ein
 * Einbauort, der ausdrücklich nur lesen will (`nurLesend` — der Lese-Kasten der
 * Rechnung, der neben dem Bearbeiten-Panel steht).
 *
 * Zuerst, was FEHLT, dann das Vorhandene (Hausregel „Schlechtestes oben"): An einem
 * festgeschriebenen Beleg ist eine unbelegte Pflichtrolle das, was jemanden aufsuchen
 * lässt — sie unten anzuhängen hiesse, sie hinter dem Erwartbaren zu verstecken.
 */
function ParteienLesend(
  { stand, mitBestellnummer }: { stand: BelegParteienStand; mitBestellnummer: boolean },
): ReactElement {
  // Am festgeschriebenen Beleg ohne Wahl der Rechnungs-E-Mail gilt `customer.email` wie vor dem Paket
  // (Gesamtreview W1) — das ist keine fehlende Angabe, die Zeile entfällt dort.
  const fehlend = stand.wahl.filter((w) => w.gewaehlt === null
    && !(stand.eingefroren && w.feld === "billingEmailAddressId"));
  const belegt = stand.wahl.filter((w) => w.gewaehlt !== null);
  const inSection = useInSection();
  return (
    <div className={inSection ? "bpa bpa--in-section" : "bpa"} role="group" aria-label={SECTION_TEXT.parties}>
      {!inSection && <h4 className="bpa__ueberschrift">{SECTION_TEXT.parties}</h4>}
      {/* Der Satz gilt dem BELEG, nicht der Ansicht: Ein noch nicht festgeschriebener
          Beleg, der hier bloss lesend gezeigt wird (`nurLesend`), bekäme sonst eine
          Zusage, die für ihn nicht stimmt. */}
      {stand.eingefroren && (
        <Hinweis art="info" className="bpa__fest">
          Dieser Beleg ist festgeschrieben — die Anschriften stehen so, wie sie beim
          Festschreiben galten.
        </Hinweis>
      )}
      {fehlend.length > 0 && (
        <ul className="bpa__fehlt">
          {fehlend.map((w) => <li key={w.feld}>{platzFuer(w.feld).leer}</li>)}
        </ul>
      )}
      <dl className="bpa__lesend">
        <div className="bpa__paar">
          <dt>Käufer</dt>
          <dd>{stand.billingCustomerName === null ? "Der Kunde selbst"
            : kaeuferEintrag(stand.billingCustomerName, stand.billingCustomerArchiviert)}</dd>
        </div>
        {belegt.map((w) => (
          <div className="bpa__paar" key={w.feld}>
            <dt>{platzFuer(w.feld).name}</dt>
            <dd>{alsBlock(w.gewaehlt!)}</dd>
          </div>
        ))}
        {mitBestellnummer && (
          <div className="bpa__paar">
            <dt>Bestellnummer des Kunden</dt>
            <dd>{stand.bestellnummer ?? "—"}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

/**
 * Ist DIESER Käufer archiviert? Zwei Quellen, weil zwei Fälle zu warnen sind: der Kandidat,
 * den jemand gerade gewählt hat (`kaeuferKandidaten.archiviert`), und der GESPEICHERTE
 * Käufer, den die Kandidatenliste gar nicht nennt (`billingCustomerArchiviert`) — der für
 * diesen Bearbeiter nicht mehr einsehbare Käufer, den F4 am Beleg stehen lässt (I2).
 *
 * `false` für „der Kunde selbst": Es gibt dann keinen zweiten Kunden, über den zu warnen
 * wäre.
 *
 * ODER und nicht „die eine Quelle, sonst die andere": Beide lesen am Server dieselbe
 * Kundenzeile und können sich nicht widersprechen — sagt eine trotzdem „archiviert", ist
 * das Warnen die richtige Hälfte des Irrtums. Eine fehlende Warnung fällt niemandem auf.
 */
function kaeuferArchiviert(stand: BelegParteienStand, kaeuferId: string | null): boolean {
  if (kaeuferId === null) return false;
  if (stand.kaeuferKandidaten.some((k) => k.id === kaeuferId && k.archiviert)) return true;
  return kaeuferId === stand.billingCustomerId && stand.billingCustomerArchiviert;
}

/** Der Name eines Käufers, wie ihn die Maske zeigt — mit „(archiviert)", wenn er es ist.
 *  EINE Stelle für Auswahlfeld und Lesefassung: Zwei Schreibweisen desselben Zusatzes
 *  wären zwei Zustände für den, der beide Ansichten desselben Belegs sieht. */
function kaeuferEintrag(name: string, archiviert: boolean): string {
  return archiviert ? `${name} (archiviert)` : name;
}

/** Der Entwurf, wie ihn der geladene Stand vorgibt: die GEWÄHLTEN Verknüpfungen, nicht die
 *  Rückfälle — ein Rückfall ist keine Wahl, und ihn als eine einzutragen schriebe ihn beim
 *  nächsten Speichern fest. */
function entwurfAus(stand: BelegParteienStand): BelegParteienEntwurf {
  const gewaehlt = (feld: string): string | null =>
    stand.wahl.find((w) => w.feld === feld)?.gewaehlt?.id ?? null;
  return {
    billingCustomerId: stand.billingCustomerId,
    billingAddressId: gewaehlt("billingAddressId"),
    billingEmailAddressId: gewaehlt("billingEmailAddressId"),
    deliveryAddressId: gewaehlt("deliveryAddressId"),
    ilnAddressId: gewaehlt("ilnAddressId"),
    purchaseOrderNumber: stand.bestellnummer,
  };
}
